/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the account, help and inbox popovers.
 * @state-owner overlay host; notifications module owns inbox reads in connected mode;
 *   HelpPopover owns its own local search-query and selected-article state, ranked against the
 *   read-only workspace UI store's active section -- no persistence, no network.
 * @allowed-imports React; shared components and hooks; demo dataset; active context; auth,
 *   notifications, help, support and feedback module public APIs
 * @forbidden in-product role switching; direct API client
 * @public-api WorkspaceRail
 * @side-effects sign-out mutation; navigation; opens the SH-F07 support modal and the SH-F08
 *   feedback modal via the shared overlay host (HelpPopover itself owns no support-case or
 *   feedback state -- SupportModal and FeedbackModal do)
 * @decision The audience login URL owns the role, so the account menu has no role-switch action.
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts;
 *   frontend/apps/web/e2e/sh-f06.spec.ts; frontend/apps/web/e2e/sh-f07.spec.ts;
 *   frontend/apps/web/e2e/sh-f08.spec.ts; ShellPopovers.test.tsx
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveContextId, useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../app/providers/SessionProvider';
import { useSignOut } from '../../modules/auth';
import { FeedbackModal } from '../../modules/feedback';
import { helpArticles, searchArticles } from '../../modules/help';
import {
  getInboxItemTargetPath,
  useInbox,
  useMarkAllInboxRead,
  useMarkInboxRead,
} from '../../modules/notifications';
import { localizedProductUpdateText, useProductUpdatesCursor } from '../../modules/product-updates';
import { SupportModal } from '../../modules/support';
import { MainEmpty } from '../../shared/components/ListColumn';
import { useOverlay } from '../../shared/components/OverlayHost';
import { PopoverIcon, type PopoverIconName, SearchIcon } from '../../shared/components/VispoIcon';
import { VispoLoading } from '../../shared/components/WorkspaceState';
import type { WorkspaceSection } from '../../shared/lib/model';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

const TEACHER_ACCOUNT_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['gear', '設定'],
  ['team', '我的補習班'],
  ['heart', '推薦好友'],
  ['plan', '方案與付款'],
  ['clock', '我的操作紀錄'],
  ['cup', '成就'],
  ['bell', '通知設定'],
];

const STUDENT_ACCOUNT_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['user', '設定'],
  ['heart', '邀請同學'],
  ['cup', '成就與徽章'],
  ['clock', '我的學習紀錄'],
  ['bell', '通知設定'],
];

// Notification target_path values are full URLs (e.g. `/teacher/classes/{id}`) with no matching
// per-resource route -- only the section-level route exists, so this resolves to the section and
// leaves entity selection to the section's own navigation, rather than risking an unmatched path
// falling through to the router's catch-all redirect.
const SECTION_PATH_PREFIXES: readonly (readonly [string, WorkspaceSection])[] = [
  ['/teacher/classes', 'room'],
  ['/student/classes', 'room'],
  ['/teacher/activities', 'act'],
  ['/student/activities', 'act'],
  ['/teacher/live', 'live'],
  ['/teacher/reports', 'rep'],
  ['/student/reports', 'rep'],
];

function resolveInboxItemSection(targetPath: string | undefined): WorkspaceSection | null {
  if (!targetPath) return null;
  return SECTION_PATH_PREFIXES.find(([prefix]) => targetPath.startsWith(prefix))?.[1] ?? null;
}

const HELP_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['book', '說明中心'],
  ['spark', '產品更新'],
  ['mega', '功能許願池'],
  ['users', '老師社群'],
  ['badge', '教學認證'],
  ['help', '聯絡客服'],
];

// SH-F06 owns 說明中心 (the popover's own real search+article content, not a row that toasts);
// SH-F07 owns 聯絡客服 (now opens the real support-case modal, rendered separately below --
// upgraded from SH-F06's own interim mailto destination, discharging that card's inherited
// obligation). SH-F08 owns 功能許願池 (now opens the real feedback modal, rendered separately
// below). The remaining three rows belong to sibling cards (SH-F09 or unclaimed) and stay exactly
// the notify()-stub placeholders they are today -- CONTRACT APPROVED R3.
const OTHER_HELP_ROWS = HELP_ROWS.filter(
  ([, label]) => label !== '說明中心' && label !== '聯絡客服' && label !== '功能許願池',
);

export function AccountPopover() {
  const navigate = useNavigate();
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const { session } = useWorkspaceSession();
  const accountLabel = session?.displayName || session?.email || (teacher ? '老師' : '學生');
  const t = useTranslator();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const { closePopover } = useOverlay();
  const signOut = useSignOut();
  const [signOutFailed, setSignOutFailed] = useState(false);
  const rows = teacher ? TEACHER_ACCOUNT_ROWS : STUDENT_ACCOUNT_ROWS;

  const attemptSignOut = () => {
    setSignOutFailed(false);
    signOut.mutate(undefined, {
      onSuccess: () => {
        closePopover();
        notify(t('已登出'));
      },
      onError: () => setSignOutFailed(true),
    });
  };

  return (
    <>
      <div className="phd2" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="av lg p">{accountLabel.trim().charAt(0).toUpperCase()}</div>
        <div>
          <b>{accountLabel}</b>
          <div style={{ fontSize: 14, color: 'var(--outline)' }}>{teacher ? '老師' : '學生'}</div>
        </div>
      </div>
      <div className="sep" />
      {rows.map(([icon, label]) => (
        <button
          className="pi"
          key={label}
          type="button"
          onClick={() => {
            closePopover();
            if (label === '設定')
              navigate(teacher ? '/teacher/settings/account' : '/student/settings');
            else notify(t(label));
          }}
        >
          <PopoverIcon name={icon} />
          {t(label)}
        </button>
      ))}
      <div className="sep" />
      <button className="pi" disabled={signOut.isPending} type="button" onClick={attemptSignOut}>
        <PopoverIcon name="out" />
        {t('登出')}
      </button>
      {signOutFailed ? (
        <div className="phd2" role="alert" style={{ color: 'var(--error)', fontSize: 13 }}>
          {t('登出失敗，請重試')}
          <button className="pi" type="button" onClick={attemptSignOut}>
            {t('重試')}
          </button>
        </div>
      ) : null}
    </>
  );
}

export function HelpPopover() {
  const t = useTranslator();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const section = useWorkspaceUiStore((state) => state.section);
  const { closePopover, openModal } = useOverlay();
  const [query, setQuery] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const results = searchArticles(helpArticles, query, section);
  const selectedArticle = helpArticles.find((article) => article.id === selectedArticleId);

  if (selectedArticle) {
    return (
      <>
        <div className="phd2">
          <button className="pi" type="button" onClick={() => setSelectedArticleId(null)}>
            {t('返回列表')}
          </button>
        </div>
        <div className="inbx" style={{ overflowWrap: 'anywhere' }}>
          <div className="c3">
            <b>{selectedArticle.title}</b>
            <p>{selectedArticle.body}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="phd2">
        <b>{t('需要協助？')}</b>
      </div>
      <label className="srchb">
        <SearchIcon />
        <input
          placeholder={t('搜尋說明文章')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="inbx">
        {results.length > 0 ? (
          results.map((article) => (
            <button
              className="nrow"
              key={article.id}
              type="button"
              onClick={() => setSelectedArticleId(article.id)}
            >
              <div className="c3">
                <b>{article.title}</b>
              </div>
            </button>
          ))
        ) : (
          <MainEmpty
            description={t('請透過下方的聯絡客服，我們會盡快回覆。')}
            title={t('找不到相關文章')}
          />
        )}
      </div>
      <button
        className="pi"
        type="button"
        onClick={() => {
          closePopover();
          openModal(<SupportModal />);
        }}
      >
        <PopoverIcon name="help" />
        {t('聯絡客服')}
      </button>
      <button
        className="pi"
        type="button"
        onClick={() => {
          closePopover();
          openModal(<FeedbackModal />);
        }}
      >
        <PopoverIcon name="mega" />
        {t('功能許願池')}
      </button>
      {OTHER_HELP_ROWS.map(([icon, label]) => (
        <button
          className="pi"
          key={label}
          type="button"
          onClick={() => {
            closePopover();
            notify(t(label));
          }}
        >
          <PopoverIcon name={icon} />
          {t(label)}
        </button>
      ))}
    </>
  );
}

export function InboxPopover({ onNavigate }: { onNavigate(section: WorkspaceSection): void }) {
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const t = useTranslator();
  const { closePopover } = useOverlay();
  const contextId = useActiveContextId();
  const { session } = useWorkspaceSession();
  const [tab, setTab] = useState<'in' | 'nw'>('in');
  const inboxQuery = useInbox(contextId, session?.portalAudience, true);
  const markRead = useMarkInboxRead(contextId, session?.portalAudience);
  const markAllRead = useMarkAllInboxRead(contextId, session?.portalAudience);
  const inbox = inboxQuery.data?.items ?? [];
  const hasUnread = inbox.some((item) => !item.isRead);
  const productUpdates = useProductUpdatesCursor();
  const language = useWorkspaceUiStore((state) => state.language);

  // SH-F09: mark-seen fires when the popover closes (this component unmounts -- OverlayHost only
  // ever renders `popover.content` while open) while '有什麼新的' was the active tab, never merely
  // on opening it. Refs carry the latest tab/markAllSeen into the unmount-only cleanup so this
  // never fires on an intermediate render, only once, at close time.
  const latestTab = useRef(tab);
  latestTab.current = tab;
  const latestMarkAllSeen = useRef(productUpdates.markAllSeen);
  latestMarkAllSeen.current = productUpdates.markAllSeen;
  useEffect(() => {
    return () => {
      if (latestTab.current === 'nw') latestMarkAllSeen.current();
    };
  }, []);

  return (
    <>
      <div className="ptabs">
        <button
          className={tab === 'in' ? 'on' : ''}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setTab('in');
          }}
        >
          {t('收件匣')}
        </button>
        <button
          className={tab === 'nw' ? 'on' : ''}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setTab('nw');
          }}
        >
          {t('有什麼新的')}
        </button>
      </div>
      {tab === 'in' && hasUnread ? (
        <div className="inbx-toolbar">
          <button
            className="inbx-mark-all"
            disabled={markAllRead.isPending}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              markAllRead.mutate();
            }}
          >
            {t('全部標為已讀')}
          </button>
        </div>
      ) : null}
      <div className="inbx">
        {tab === 'in' && inboxQuery.isLoading ? (
          <VispoLoading className="inbox-loading" label={t('正在載入收件匣')} />
        ) : null}
        {tab === 'in'
          ? inbox.map((item) => (
              <button
                className="nrow"
                key={item.id}
                type="button"
                onClick={() => {
                  markRead.mutate(item);
                  closePopover();
                  const section = resolveInboxItemSection(getInboxItemTargetPath(item));
                  onNavigate(section ?? (teacher ? 'room' : 'act'));
                }}
              >
                <span className={`d2 ${item.isUrgent || !item.isRead ? '' : 'g'}`} />
                <div className="c3">
                  <b>{item.title}</b>
                  <p>{item.subtitle}</p>
                  <em>{new Date(item.timestamp).toLocaleString()}</em>
                </div>
              </button>
            ))
          : null}
        {tab === 'in' && !inboxQuery.isLoading && inbox.length === 0 ? (
          <MainEmpty description={t('新的繳交與提醒會顯示在這裡。')} title={t('收件匣是空的')} />
        ) : null}
        {tab === 'nw' && productUpdates.updates.length === 0 ? (
          <MainEmpty description={t('新的功能公告會顯示在這裡。')} title={t('目前沒有產品更新')} />
        ) : null}
        {tab === 'nw'
          ? productUpdates.updates.map((update) => (
              <div className="nrow" key={update.id}>
                <span className={productUpdates.unseenIds.has(update.id) ? 'd2' : 'd2 g'} />
                <div className="c3" style={{ overflowWrap: 'anywhere' }}>
                  <b>{localizedProductUpdateText(update.title, language)}</b>
                  <p>{localizedProductUpdateText(update.body, language)}</p>
                  <em>{new Date(update.publishedAt).toLocaleDateString()}</em>
                </div>
              </div>
            ))
          : null}
      </div>
    </>
  );
}
